import { PayloadAction } from "@reduxjs/toolkit";
import { call, delay, put, StrictEffect, takeLatest } from "redux-saga/effects";

import { FeatureType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";
import { TransactionComment } from "@/jenesys-types/jenesys-entities/config";

import { performFeatureAction } from "@/services/api";

import {
  addComment,
  addCommentFailure,
  AddCommentPayload,
  addCommentSuccess,
  deleteComment,
  deleteCommentFailure,
  DeleteCommentPayload,
  deleteCommentSuccess,
  editComment,
  editCommentFailure,
  EditCommentPayload,
  editCommentSuccess,
  loadComments,
  loadCommentsFailure,
  LoadCommentsPayload,
  loadCommentsSuccess
} from "./comments";

function* loadCommentsSaga(
  action: PayloadAction<LoadCommentsPayload>
): Generator<StrictEffect, void, any> {
  const { documentId, companyId } = action.payload;

  try {
    const response: Array<TransactionComment> = yield call(
      performFeatureAction,
      FeatureType.Bookkeeping,
      "loadTransactionComments",
      { documentId, companyId }
    );

    yield put(loadCommentsSuccess(documentId, response));
  } catch (error) {
    yield put(loadCommentsFailure(documentId, extractError(error)));
  }
}

function* addCommentSaga(
  action: PayloadAction<AddCommentPayload>
): Generator<StrictEffect, void, any> {
  const { documentId, companyId, comment } = action.payload;

  try {
    const response: TransactionComment = yield call(
      performFeatureAction,
      FeatureType.Bookkeeping,
      "addTransactionComment",
      { documentId, companyId },
      undefined,
      { comment }
    );

    yield put(addCommentSuccess(documentId, response));
    yield put(loadComments(documentId, companyId));
  } catch (error) {
    yield put(addCommentFailure(documentId, extractError(error)));
  }
}

function* editCommentSaga(
  action: PayloadAction<EditCommentPayload>
): Generator<StrictEffect, void, any> {
  const { documentId, companyId, commentId, comment } = action.payload;

  try {
    const response: TransactionComment = yield call(
      performFeatureAction,
      FeatureType.Bookkeeping,
      "editTransactionComment",
      { documentId, companyId, commentId },
      undefined,
      { comment }
    );

    yield put(editCommentSuccess(documentId, response));

    yield delay(300);

    yield put(loadComments(documentId, companyId));
  } catch (error) {
    yield put(editCommentFailure(documentId, commentId, extractError(error)));
  }
}

function* deleteCommentSaga(
  action: PayloadAction<DeleteCommentPayload>
): Generator<StrictEffect, void, any> {
  const { documentId, companyId, commentId } = action.payload;

  try {
    yield call(
      performFeatureAction,
      FeatureType.Bookkeeping,
      "deleteTransactionComment",
      { documentId, companyId, commentId }
    );

    yield put(deleteCommentSuccess(documentId, commentId));

    yield put(loadComments(documentId, companyId));
  } catch (error) {
    yield put(deleteCommentFailure(documentId, commentId, extractError(error)));
  }
}

export default function* commentsSaga() {
  yield takeLatest(loadComments.type, loadCommentsSaga);
  yield takeLatest(addComment.type, addCommentSaga);
  yield takeLatest(editComment.type, editCommentSaga);
  yield takeLatest(deleteComment.type, deleteCommentSaga);
}
